// src/components/Center Admin/forms/CariogramInputForm.tsx
import React, { useEffect, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/CariogramInputForm.css"; // ✅ Scoped styles
import "../../../center admin css/centerAdmin.css";

interface FormComponentProps {
  emrId: string;
  submitWorkflow?: boolean;
  onComplete?: () => void;
}

export const CariogramInputForm: React.FC<FormComponentProps> = ({
  emrId,
  submitWorkflow = false,
  onComplete,
}) => {
  const [lacto, setLacto] = useState<number | null>(null);
  const [strepto, setStrepto] = useState<number | null>(null);
  const [secretion, setSecretion] = useState<string | null>(null);
  const [buffer, setBuffer] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  console.log("The EMR ID for this form is:", emrId);
  const resetForm = () => {
    setLacto(null);
    setStrepto(null);
    setSecretion(null);
    setBuffer(null);
  };

  /* ---------------------------------------------------------
      LOAD EXISTING CARIOGRAM INPUT
  ---------------------------------------------------------- */

  const loadExistingCariogram = async () => {
    if (!emrId) return;

    try {
      const res = await api.dataCollectionApi.get(
        `/api/forms/cariogram-saliva-details/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data;

      if (!saved) {
        resetForm();
        return;
      }

      const savedLacto =
        saved["Lactobacilli in Saliva"]?.[
          "Lactobacilli – Diet and Carbohydrate Exposure Assessment"
        ];

      const savedStrepto =
        saved["Streptococci Mutans in Saliva"]?.[
          "Streptococci Mutans – Bacterial Load on Teeth"
        ];

      const savedSecretion =
        saved["Saliva Secretion Rate"]?.[
          "Saliva Secretion – Stimulated Flow Rate per Minute"
        ];

      const savedBuffer =
        saved["Saliva Buffer Capacity (pH)"]?.[
          "Buffering Capacity – Dentobuff Color Result"
        ];

      setLacto(
        typeof savedLacto === "string" && savedLacto.startsWith("Class ")
          ? Number(savedLacto.replace("Class ", ""))
          : null,
      );

      setStrepto(
        typeof savedStrepto === "string" && savedStrepto.startsWith("Class ")
          ? Number(savedStrepto.replace("Class ", ""))
          : null,
      );

      setSecretion(savedSecretion ?? null);
      setBuffer(savedBuffer ?? null);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        resetForm();
        return;
      }

      console.error("Failed to load existing Cariogram Input:", err);
    }
  };

  useEffect(() => {
    loadExistingCariogram();
  }, [emrId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      emrId,
      "Lactobacilli in Saliva": {
        "Lactobacilli – Diet and Carbohydrate Exposure Assessment": lacto
          ? `Class ${lacto}`
          : undefined,
      },
      "Streptococci Mutans in Saliva": {
        "Streptococci Mutans – Bacterial Load on Teeth":
          strepto !== null ? `Class ${strepto}` : undefined,
      },
      "Saliva Secretion Rate": {
        "Saliva Secretion – Stimulated Flow Rate per Minute":
          secretion ?? undefined,
      },
      "Saliva Buffer Capacity (pH)": {
        "Buffering Capacity – Dentobuff Color Result": buffer ?? undefined,
      },
    };

    try {
      await api.dataCollectionApi.post(
        "/api/forms/cariogram-saliva-details",
        payload,
      );

      // alert("Cariogram submitted successfully!");
      console.log(
        "workflowSessionId:",
        sessionStorage.getItem("workflowSessionId"),
      );

      if (submitWorkflow) {
        const workflowSessionId = sessionStorage.getItem("workflowSessionId");

        if (!workflowSessionId) {
          throw new Error("workflowSessionId missing");
        }

        await api.centerworkflowApi.post(
          `/sessions/${workflowSessionId}/center-admin/forms/CARIOGRAM_INPUT/submit`,
        );

        // sessionStorage.removeItem("selectedPatient_CARIOGRAM_Q");
      }

      onComplete?.();

      window.dispatchEvent(new Event("patientUpdated"));

      alert("Cariogram assessment submitted successfully!");

      resetForm();

      // alert("Cariogram submitted successfully!");
    } catch (err: any) {
      console.error(err);
      alert(
        "Failed to submit Cariogram: " +
          (err.response?.data?.message ?? err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="cariogram-form" onSubmit={handleSubmit}>
      <div className="cariogram-header">
        <div className="vs-section-bar">Cariogram Input</div>
      </div>

      {/* ------------------------------- */}
      {/* SECTION 1 — Lactobacilli */}
      {/* ------------------------------- */}
      <section className="cario-section">
        <h4 className="cario-section-title">Lactobacilli in Saliva</h4>
        <p className="cario-section-subtitle">
          Lactobacilli – Diet and Carbohydrate Exposure Assessment
        </p>

        <div className="cariogram-options lacto-grid">
          {[
            {
              id: 1,
              title: "Class 1",
              desc: "Extremely good diet with caries-inducing carbohydrate on a very low level",
            },
            {
              id: 2,
              title: "Class 2",
              desc: "Appropriate diet. Sugars or other caries-inducing carbohydrate on a low level",
            },
            {
              id: 3,
              title: "Class 3",
              desc: "Diet with relatively high content of sugars or other caries-inducing carbohydrate",
            },
            {
              id: 4,
              title: "Class 4",
              desc: "Inappropriate diet. High intake of sugars or other caries-inducing carbohydrate",
            },
          ].map((opt) => (
            <label
              key={opt.id}
              className={`cariogram-option ${
                lacto === opt.id ? "selected" : ""
              }`}
            >
              <div className="cario-radio">
                <input
                  type="radio"
                  name="lacto"
                  checked={lacto === opt.id}
                  onChange={() => setLacto(opt.id)}
                />
                <span className="radio-custom"></span>

                <div className="option-content">
                  <strong>{opt.title}</strong>
                  <span>{opt.desc}</span>
                </div>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* ------------------------------- */}
      {/* SECTION 2 — Streptococci */}
      {/* ------------------------------- */}
      <section className="cario-section">
        <h4 className="cario-section-title">Streptococci Mutans in Saliva</h4>
        <p className="cario-section-subtitle">
          Streptococci Mutans – Bacterial Load on Teeth
        </p>

        <div className="cariogram-options strepto-grid">
          {[
            {
              id: 0,
              title: "Class 0",
              desc: "Very low or zero mutans streptococci. ~5% of tooth surface colonised",
            },
            {
              id: 1,
              title: "Class 1",
              desc: "Low levels. ~20% of tooth surfaces colonised",
            },
            { id: 2, title: "Class 2", desc: "High amount. ~60% colonised" },
            {
              id: 3,
              title: "Class 3",
              desc: "Very high amount. ~80% tooth surfaces colonised",
            },
          ].map((opt) => (
            <label
              key={opt.id}
              className={`cariogram-option ${
                strepto === opt.id ? "selected" : ""
              }`}
            >
              <div className="cario-radio">
                <input
                  type="radio"
                  name="strepto"
                  checked={strepto === opt.id}
                  onChange={() => setStrepto(opt.id)}
                />
                <span className="radio-custom"></span>

                <div className="option-content">
                  <strong>{opt.title}</strong>
                  <span>{opt.desc}</span>
                </div>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* ------------------------------- */}
      {/* SECTION 3 — Saliva Secretion */}
      {/* ------------------------------- */}
      <section className="cario-section">
        <h4 className="cario-section-title">Saliva Secretion Rate</h4>
        <p className="cario-section-subtitle">
          Saliva Secretion – Stimulated Flow Rate per Minute
        </p>

        <div className="cariogram-options secretion-grid">
          {[
            { id: "Normal", title: "Normal", desc: "> 1.1 ml/min" },
            { id: "Mildly Low", title: "Mildly Low", desc: "0.9 – 1.1 ml/min" },
            {
              id: "Moderately Low",
              title: "Moderately Low",
              desc: "0.5 – 0.9 ml/min",
            },
            {
              id: "Severely Low",
              title: "Severely Low",
              desc: "< 0.5 ml/min (dry mouth)",
            },
          ].map((opt) => (
            <label
              key={opt.id}
              className={`cariogram-option ${
                secretion === opt.id ? "selected" : ""
              }`}
            >
              <div className="cario-radio">
                <input
                  type="radio"
                  name="secretion"
                  checked={secretion === opt.id}
                  onChange={() => setSecretion(opt.id)}
                />
                <span className="radio-custom"></span>

                <div className="option-content">
                  <strong>{opt.title}</strong>
                  <span>{opt.desc}</span>
                </div>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* ------------------------------- */}
      {/* SECTION 4 — Buffer Capacity */}
      {/* ------------------------------- */}
      <section className="cario-section">
        <h4 className="cario-section-title">Saliva Buffer Capacity (pH)</h4>
        <p className="cario-section-subtitle">
          Buffering Capacity – Dentobuff Color Result
        </p>

        <div className="cariogram-options">
          {[
            {
              id: "Adequate",
              title: "Adequate",
              desc: "Dentobuff Blue, end-pH ≥ 6.0",
            },
            {
              id: "Reduced",
              title: "Reduced",
              desc: "Dentobuff Green, end-pH 4.5–5.5",
            },
            { id: "Low", title: "Low", desc: "Dentobuff Yellow, end-pH ≤ 4.0" },
          ].map((opt) => (
            <label
              key={opt.id}
              className={`cariogram-option ${
                buffer === opt.id ? "selected" : ""
              }`}
            >
              <div className="cario-radio">
                <input
                  type="radio"
                  name="buffer"
                  checked={buffer === opt.id}
                  onChange={() => setBuffer(opt.id)}
                />
                <span className="radio-custom"></span>

                <div className="option-content">
                  <strong>{opt.title}</strong>
                  <span>{opt.desc}</span>
                </div>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>
        <button
          type="submit"
          className="audio-btn audio-btn-primary"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Assessment"}
        </button>
      </div>
    </form>
  );
};

export default CariogramInputForm;
